/**
 * React Query hooks for Adventure Files/Media Management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adventureFilesApi } from '@api/endpoints/adventureFiles'
import type {
  GetAdventureFilesParams,
  UpdateAdventureMediaFileRequest,
  SelectAdventureFileRequest,
  AdventureFileUploadOptions,
} from '@api/types/adventureFiles'

// ===================
// Query Keys
// ===================

export const ADVENTURE_FILE_QUERY_KEYS = {
  all: (adventureId: string) => ['adventure-files', adventureId] as const,
  list: (adventureId: string, params?: GetAdventureFilesParams) =>
    [...ADVENTURE_FILE_QUERY_KEYS.all(adventureId), 'list', params] as const,
  detail: (adventureId: string, fileId: string) =>
    [...ADVENTURE_FILE_QUERY_KEYS.all(adventureId), 'detail', fileId] as const,
  usages: (adventureId: string, fileId: string) =>
    [...ADVENTURE_FILE_QUERY_KEYS.all(adventureId), 'usages', fileId] as const,
}

// ===================
// Query Hooks
// ===================

/**
 * Get list of files for an adventure with filtering and pagination
 */
export function useAdventureFiles(adventureId: string | null, params?: GetAdventureFilesParams) {
  return useQuery({
    queryKey: ADVENTURE_FILE_QUERY_KEYS.list(adventureId || '', params),
    queryFn: () => adventureFilesApi.getFiles(adventureId!, params),
    enabled: !!adventureId,
  })
}

/**
 * Get detailed information about a specific file
 */
export function useAdventureFile(adventureId: string | null, fileId: string | null) {
  return useQuery({
    queryKey: ADVENTURE_FILE_QUERY_KEYS.detail(adventureId || '', fileId || ''),
    queryFn: () => adventureFilesApi.getFile(adventureId!, fileId!),
    enabled: !!adventureId && !!fileId,
  })
}

/**
 * Get usages of a file within the adventure
 */
export function useAdventureFileUsages(adventureId: string | null, fileId: string | null) {
  return useQuery({
    queryKey: ADVENTURE_FILE_QUERY_KEYS.usages(adventureId || '', fileId || ''),
    queryFn: () => adventureFilesApi.getFileUsages(adventureId!, fileId!),
    enabled: !!adventureId && !!fileId,
  })
}

// ===================
// Mutation Hooks
// ===================

/**
 * Upload a new file to the adventure
 */
export function useUploadAdventureFile(adventureId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ file, options }: { file: File; options?: AdventureFileUploadOptions }) => {
      if (!adventureId) throw new Error('Adventure ID is required')
      return adventureFilesApi.uploadFile(adventureId, file, options)
    },
    onSuccess: () => {
      if (adventureId) {
        queryClient.invalidateQueries({
          queryKey: ADVENTURE_FILE_QUERY_KEYS.all(adventureId),
        })
      }
    },
  })
}

/**
 * Select/link an existing file to the adventure
 */
export function useSelectAdventureFile(adventureId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SelectAdventureFileRequest) => {
      if (!adventureId) throw new Error('Adventure ID is required')
      return adventureFilesApi.selectFile(adventureId, data)
    },
    onSuccess: () => {
      if (adventureId) {
        queryClient.invalidateQueries({
          queryKey: ADVENTURE_FILE_QUERY_KEYS.all(adventureId),
        })
      }
    },
  })
}

/**
 * Update file metadata
 */
export function useUpdateAdventureFile(adventureId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ fileId, data }: { fileId: string; data: UpdateAdventureMediaFileRequest }) => {
      if (!adventureId) throw new Error('Adventure ID is required')
      return adventureFilesApi.updateFile(adventureId, fileId, data)
    },
    onSuccess: (updatedFile, { fileId }) => {
      if (adventureId) {
        // Update the specific file in the cache
        queryClient.setQueryData(
          ADVENTURE_FILE_QUERY_KEYS.detail(adventureId, fileId),
          updatedFile
        )
        // Invalidate the list to refresh
        queryClient.invalidateQueries({
          queryKey: ADVENTURE_FILE_QUERY_KEYS.all(adventureId),
        })
      }
    },
  })
}

/**
 * Delete a file from the adventure
 */
export function useDeleteAdventureFile(adventureId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileId: string) => {
      if (!adventureId) throw new Error('Adventure ID is required')
      return adventureFilesApi.deleteFile(adventureId, fileId)
    },
    onSuccess: () => {
      if (adventureId) {
        queryClient.invalidateQueries({
          queryKey: ADVENTURE_FILE_QUERY_KEYS.all(adventureId),
        })
      }
    },
  })
}
